import { createSelector, createSelectorFactory, resultMemoize, Selector, select } from '@ngrx/store';
import { Observable, pipe, filter } from 'rxjs';

import { isDeepEqual, isPresent } from '@fcom/core/utils/utils';

export type StateSelector<A, B> = Selector<Observable<A>, Observable<B>>;

export function safeSelect<T, K>(mapFn: (state: T) => K): (source$: Observable<T>) => Observable<K> {
  return pipe(select(mapFn), filter(isPresent));
}

/**
 * Creates a selector which doesn't emit a new value when its output loses referential equality but keeps deep equality.
 * Regular selectors memoize based on input args, but a selector which creates complex derived objects will emit a new
 * instance every time it executes and thus any downstream selectors etc. execute as they receive a new
 * input object. This doesn't skip initial work (this still executes its own code every time its input changes) and
 * adds the deep equality check overhead on top of it so use sparingly.
 */
export const createDeepEqualSelector = createSelectorFactory((projector) =>
  resultMemoize(projector, isDeepEqual)
) as typeof createSelector;
